import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, X, Trophy, Users, ArrowLeft, ChevronRight, Clock, Award, Lightbulb, History, ClipboardList, Crown, FileDown, Shield, Smartphone, Copy, Eye, EyeOff, Sparkles, KeyRound, Save, Trash2, AlertTriangle } from "lucide-react";
import ETSGauge from "../components/ETSGauge";
import { AttendanceAdminPanel, BehaviourAdminPanel, InnovationAdminPanel, LoginHistoryPanel, TasksAdminPanel, CeoReportPanel } from "./AdminEmployeePanels";
import "./employee.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPARTMENTS = ["Sales", "Warehouse", "Purchase", "Accounts", "Customer Support", "Marketing", "Management", "Delivery", "Showroom"];
const STATUSES = ["Active", "Inactive", "Suspended", "Resigned"];

function adminAuthHeaders() {
  const t = localStorage.getItem("dtile_admin_token");
  return { headers: { Authorization: `Bearer ${t}` } };
}

export default function AdminEmployees({ onBack }) {
  const [view, setView] = useState("list"); // list | add | profile | attendance | behaviour | innovation | audit
  const [selectedId, setSelectedId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("ets_desc");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees`, adminAuthHeaders());
      setEmployees(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employees");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = [...employees];
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(e => (e.name || "").toLowerCase().includes(s)
        || (e.employee_code || "").toLowerCase().includes(s)
        || (e.mobile || "").includes(s));
    }
    if (filterDept !== "all") list = list.filter(e => (e.departments || []).includes(filterDept));
    if (filterStatus !== "all") list = list.filter(e => e.employment_status === filterStatus);
    switch (sortBy) {
      case "ets_asc": list.sort((a, b) => (a.ets_score || 0) - (b.ets_score || 0)); break;
      case "name_asc": list.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "recent": list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      default: list.sort((a, b) => (b.ets_score || 0) - (a.ets_score || 0));
    }
    return list;
  }, [employees, q, filterDept, filterStatus, sortBy]);

  if (view === "add") return <AddEmployeeForm onSaved={() => { setView("list"); load(); }} onCancel={() => setView("list")} employees={employees} />;
  if (view === "profile" && selectedId) return <EmployeeProfile id={selectedId} onBack={() => { setView("list"); load(); }} employees={employees} />;

  const subNav = (
    <div className="emp-glass p-2 mb-6 flex flex-wrap gap-1" data-testid="emp-subnav">
      {[
        { k: "list", label: "Employee List", icon: Users },
        { k: "tasks", label: "Tasks", icon: ClipboardList },
        { k: "attendance", label: "Attendance", icon: Clock },
        { k: "behaviour", label: "Behaviour", icon: Award },
        { k: "innovation", label: "Innovation", icon: Lightbulb },
        { k: "audit", label: "Login History", icon: History },
        { k: "ceo-report", label: "Weekly CEO Report", icon: Crown },
      ].map(({ k, label, icon: Icon }) => (
        <button
          key={k}
          onClick={() => setView(k)}
          className={`text-[11px] uppercase tracking-[0.15em] px-3 py-2 inline-flex items-center gap-1.5 transition-colors ${view === k ? "text-white" : "opacity-60 hover:opacity-100"}`}
          style={view === k ? { background: "var(--emp-primary)", color: "#fff" } : {}}
          data-testid={`emp-subnav-${k}`}
        >
          <Icon className="w-3 h-3" strokeWidth={1.5} /> {label}
        </button>
      ))}
    </div>
  );

  if (view === "attendance") return <div className="emp-scope">{subNav}<AttendanceAdminPanel /></div>;
  if (view === "behaviour") return <div className="emp-scope">{subNav}<BehaviourAdminPanel /></div>;
  if (view === "innovation") return <div className="emp-scope">{subNav}<InnovationAdminPanel /></div>;
  if (view === "audit") return <div className="emp-scope">{subNav}<LoginHistoryPanel /></div>;
  if (view === "tasks") return <div className="emp-scope">{subNav}<TasksAdminPanel /></div>;
  if (view === "ceo-report") return <div className="emp-scope">{subNav}<CeoReportPanel /></div>;

  return (
    <div className="emp-scope">
      {subNav}
      {/* toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          {onBack && <button onClick={onBack} className="emp-eyebrow inline-flex items-center gap-1 mb-2 opacity-70 hover:opacity-100"><ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to Admin</button>}
          <p className="emp-eyebrow">Employee Intelligence</p>
          <h2 className="font-editorial text-3xl mt-1">Employees</h2>
          <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}><Users className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> {employees.length} total · sorted by highest ETS</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              const url = `${API}/admin/exports/employees.csv`;
              fetch(url, adminAuthHeaders()).then(r => r.blob()).then(b => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(b);
                link.download = `tileroom_employees_${new Date().toISOString().slice(0, 10)}.csv`;
                link.click();
              }).catch(() => toast.error("Export failed"));
            }}
            className="emp-btn-ghost inline-flex items-center gap-2"
            data-testid="emp-export-csv"
          >
            <FileDown className="w-3.5 h-3.5" strokeWidth={1.5} /> Export CSV
          </button>
          <button onClick={() => setView("add")} className="emp-btn-primary inline-flex items-center gap-2" data-testid="emp-add-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add Employee
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="emp-glass p-4 mb-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div className="sm:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40" strokeWidth={1.5} />
          <input
            className="emp-input pl-9"
            placeholder="Search by name, employee ID or mobile…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="emp-search"
          />
        </div>
        <select className="emp-input" value={filterDept} onChange={(e) => setFilterDept(e.target.value)} data-testid="emp-filter-dept">
          <option value="all">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="emp-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="emp-filter-status">
          <option value="all">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="emp-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="emp-sort">
          <option value="ets_desc">Highest ETS</option>
          <option value="ets_asc">Lowest ETS</option>
          <option value="name_asc">Name A→Z</option>
          <option value="recent">Recently added</option>
        </select>
      </div>

      {/* grid */}
      {loading ? (
        <p className="text-center py-16 emp-eyebrow">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="emp-glass p-16 text-center">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-40" strokeWidth={1.2} />
          <p className="font-editorial text-xl">No employees yet</p>
          <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>Click "Add Employee" to onboard your first team member.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="emp-grid">
          {filtered.map(e => (
            <div key={e.id} onClick={() => { setSelectedId(e.id); setView("profile"); }} className="emp-glass p-5 cursor-pointer emp-fade-in" data-testid={`emp-card-${e.employee_code}`}>
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden border" style={{ borderColor: "var(--emp-accent)" }}>
                  {e.photo_url ? (
                    <img src={e.photo_url} alt={e.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-editorial text-lg" style={{ background: "var(--emp-accent)", color: "#1E2C5A" }}>
                      {(e.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-editorial text-lg leading-tight truncate">{e.name}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: "var(--emp-muted)" }}>{e.employee_code} · {e.designation || "—"}</p>
                  <p className="text-[10px] mt-1 emp-eyebrow" style={{ opacity: 0.7 }}>{(e.departments || []).join(", ") || "No dept"}</p>
                </div>
                <ChevronRight className="w-4 h-4 opacity-30 flex-shrink-0" strokeWidth={1.5} />
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="border-r pr-2" style={{ borderColor: "var(--emp-border)" }}>
                  <p className="emp-eyebrow" style={{ opacity: 0.6 }}>ETS</p>
                  <p className="font-editorial text-lg" style={{ color: "var(--emp-accent)" }}>{e.ets_score || 0}</p>
                </div>
                <div className="border-r pr-2" style={{ borderColor: "var(--emp-border)" }}>
                  <p className="emp-eyebrow" style={{ opacity: 0.6 }}>Grade</p>
                  <p className="text-[10px] mt-1"><span className={`emp-grade emp-grade-${(e.grade || "Bronze").replace(/\s/g, "")}`}>{e.grade || "—"}</span></p>
                </div>
                <div>
                  <p className="emp-eyebrow" style={{ opacity: 0.6 }}>Status</p>
                  <p className="text-xs mt-1"><span className={`emp-dot emp-dot-${e.current_status || "Offline"}`} />{e.current_status || "Offline"}</p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-[10px]" style={{ color: "var(--emp-muted)" }}>
                <span>Joined {e.joining_date}</span>
                <span className={`emp-grade emp-grade-${e.employment_status === "Active" ? "Gold" : "NeedsImprovement"}`}>{e.employment_status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AddEmployeeForm({ onSaved, onCancel, employees }) {
  const activeEmployees = useMemo(
    () => (employees || []).filter(e => e.employment_status === "Active"),
    [employees]
  );
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", password: "", photo_url: "",
    dob: "", gender: "", blood_group: "", address: "", emergency_contact: "",
    aadhaar: "", pan: "", joining_date: new Date().toISOString().slice(0, 10),
    departments: [], designation: "", reporting_manager_id: "", salary: "", employment_status: "Active",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("TR…");

  useEffect(() => {
    axios.get(`${API}/admin/employees/next-code`, adminAuthHeaders())
      .then(r => setPreview(r.data.employee_code))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDept = (d) => setForm(f => ({ ...f, departments: f.departments.includes(d) ? f.departments.filter(x => x !== d) : [...f.departments, d] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.password || form.password.length < 6) {
      toast.error("Name, mobile and a 6+ char password are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, salary: form.salary ? +form.salary : null };
      const { data } = await axios.post(`${API}/admin/employees`, payload, adminAuthHeaders());
      toast.success(`${data.name} added as ${data.employee_code}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  return (
    <div className="emp-scope max-w-4xl mx-auto emp-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={onCancel} className="emp-eyebrow inline-flex items-center gap-1 opacity-70 hover:opacity-100"><ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to list</button>
          <h2 className="font-editorial text-3xl mt-2">Add Employee</h2>
          <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>Employee ID will be <span className="emp-eyebrow" style={{ color: "var(--emp-accent)" }}>{preview}</span> (auto-generated)</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Identity */}
        <div className="emp-glass p-6">
          <p className="emp-eyebrow mb-4">Identity</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name *" value={form.name} onChange={v => set("name", v)} testId="emp-form-name" />
            <Field label="Mobile *" value={form.mobile} onChange={v => set("mobile", v)} placeholder="+91 98…" testId="emp-form-mobile" />
            <Field label="Email" type="email" value={form.email} onChange={v => set("email", v)} testId="emp-form-email" />
            <PasswordField
              value={form.password}
              onChange={v => set("password", v)}
              label="Password (min 6) *"
              testId="emp-form-password"
            />
            <Field label="Profile Photo URL" value={form.photo_url} onChange={v => set("photo_url", v)} placeholder="https://…" testId="emp-form-photo" />
            <Field label="Date of Birth" type="date" value={form.dob} onChange={v => set("dob", v)} />
            <div>
              <label className="emp-eyebrow block mb-2">Gender</label>
              <select className="emp-input" value={form.gender} onChange={e => set("gender", e.target.value)}>
                <option value="">Select…</option>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </div>
            <Field label="Blood Group" value={form.blood_group} onChange={v => set("blood_group", v)} placeholder="O+" />
            <div className="sm:col-span-2">
              <Field label="Address" value={form.address} onChange={v => set("address", v)} />
            </div>
            <Field label="Emergency Contact" value={form.emergency_contact} onChange={v => set("emergency_contact", v)} />
            <Field label="Aadhaar (optional)" value={form.aadhaar} onChange={v => set("aadhaar", v)} />
            <Field label="PAN (optional)" value={form.pan} onChange={v => set("pan", v)} />
          </div>
        </div>

        {/* Employment */}
        <div className="emp-glass p-6">
          <p className="emp-eyebrow mb-4">Employment</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Joining Date" type="date" value={form.joining_date} onChange={v => set("joining_date", v)} />
            <div>
              <label className="emp-eyebrow block mb-2">Employment Status</label>
              <select className="emp-input" value={form.employment_status} onChange={e => set("employment_status", e.target.value)} data-testid="emp-form-status">
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Field label="Designation" value={form.designation} onChange={v => set("designation", v)} placeholder="Sales Executive" testId="emp-form-designation" />
            <div>
              <label className="emp-eyebrow block mb-2">Reporting Manager</label>
              <select className="emp-input" value={form.reporting_manager_id} onChange={e => set("reporting_manager_id", e.target.value)}>
                <option value="">None</option>
                {activeEmployees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
              </select>
            </div>
            <Field label="Salary (Admin only)" type="number" value={form.salary} onChange={v => set("salary", v)} placeholder="Monthly ₹" testId="emp-form-salary" />
            <div className="sm:col-span-2">
              <label className="emp-eyebrow block mb-2">Departments (multi-select)</label>
              <div className="flex flex-wrap gap-2" data-testid="emp-form-departments">
                {DEPARTMENTS.map(d => (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggleDept(d)}
                    className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors"
                    style={form.departments.includes(d)
                      ? { background: "var(--emp-primary)", color: "#fff", borderColor: "var(--emp-primary)" }
                      : { borderColor: "var(--emp-border)", color: "var(--emp-muted)" }}
                    data-testid={`emp-dept-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className="emp-btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="emp-btn-primary inline-flex items-center gap-2" data-testid="emp-form-submit">
            {busy ? "Saving…" : (<><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Create Employee</>)}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, value, onChange, type = "text", placeholder = "", testId }) {
  return (
    <div>
      <label className="emp-eyebrow block mb-2">{label}</label>
      <input
        className="emp-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}


function EmployeeProfile({ id, onBack, employees }) {
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("overview");
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scoreEdit, setScoreEdit] = useState(false);
  const [newScore, setNewScore] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/employees/${id}`, adminAuthHeaders()).then(r => { setEmp(r.data); setNewScore(r.data.ets_score); });
    axios.get(`${API}/admin/employees/${id}/audit`, adminAuthHeaders()).then(r => setAudit(r.data || [])).catch(() => {});
  }, [id]);

  const saveScore = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/employees/${id}`, { ets_score: +newScore }, adminAuthHeaders());
      setEmp(data);
      toast.success("ETS updated");
      setScoreEdit(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } finally { setSaving(false); }
  };

  if (!emp) return <div className="emp-scope p-16 text-center emp-eyebrow">Loading…</div>;
  const manager = employees.find(x => x.id === emp.reporting_manager_id);

  const tabs = ["overview", "ets", "permissions", "devices", "attendance", "sales", "cx", "behaviour", "innovation", "login-history", "reports", "settings"];

  return (
    <div className="emp-scope emp-fade-in">
      <button onClick={onBack} className="emp-eyebrow inline-flex items-center gap-1 mb-4 opacity-70 hover:opacity-100"><ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to list</button>

      {/* Hero */}
      <div className="emp-glass p-6 sm:p-8 mb-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2" style={{ borderColor: "var(--emp-accent)" }}>
            {emp.photo_url ? <img src={emp.photo_url} alt={emp.name} className="w-full h-full object-cover" /> : (
              <div className="w-full h-full flex items-center justify-center font-editorial text-3xl" style={{ background: "var(--emp-accent)", color: "#1E2C5A" }}>
                {(emp.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="emp-eyebrow">{emp.employee_code} · {emp.designation || "—"}</p>
            <h2 className="font-editorial text-4xl mt-1">{emp.name}</h2>
            <p className="text-sm mt-1" style={{ color: "var(--emp-muted)" }}>
              {(emp.departments || []).join(", ") || "No department"}
              {manager && <> · Reports to <span style={{ color: "var(--emp-accent)" }}>{manager.name}</span></>}
            </p>
            <p className="text-xs mt-2" style={{ color: "var(--emp-muted)" }}>
              📱 {emp.mobile}
              {emp.email && <> · ✉ {emp.email}</>}
              <> · Joined {emp.joining_date}</>
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`emp-grade emp-grade-${(emp.grade || "Bronze").replace(/\s/g, "")}`}>{emp.grade}</span>
              <span className="text-xs"><span className={`emp-dot emp-dot-${emp.current_status || "Offline"}`} />{emp.current_status || "Offline"}</span>
              <span className={`emp-grade emp-grade-${emp.employment_status === "Active" ? "Gold" : "NeedsImprovement"}`}>{emp.employment_status}</span>
            </div>
          </div>
          <div className="flex-shrink-0"><ETSGauge score={emp.ets_score} size={140} stroke={10} label={false} /></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="emp-glass p-2 mb-4 flex flex-wrap gap-1" data-testid="emp-profile-tabs">
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 transition-colors ${tab === t ? "bg-current text-white" : "opacity-60 hover:opacity-100"}`}
            style={tab === t ? { background: "var(--emp-primary)", color: "#fff" } : {}}
            data-testid={`emp-tab-${t}`}
          >
            {t.replace(/-/g, " ")}
          </button>
        ))}
      </div>

      <div className="emp-glass p-6">
        {tab === "overview" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div><p className="emp-eyebrow mb-1">Salary (admin-only)</p><p className="text-lg">{emp.salary ? `₹${emp.salary.toLocaleString("en-IN")}` : "—"}</p></div>
            <div><p className="emp-eyebrow mb-1">Aadhaar</p><p className="text-sm">{emp.aadhaar || "—"}</p></div>
            <div><p className="emp-eyebrow mb-1">PAN</p><p className="text-sm">{emp.pan || "—"}</p></div>
            <div><p className="emp-eyebrow mb-1">DOB</p><p className="text-sm">{emp.dob || "—"}</p></div>
            <div><p className="emp-eyebrow mb-1">Gender</p><p className="text-sm">{emp.gender || "—"}</p></div>
            <div><p className="emp-eyebrow mb-1">Blood Group</p><p className="text-sm">{emp.blood_group || "—"}</p></div>
            <div className="sm:col-span-3"><p className="emp-eyebrow mb-1">Address</p><p className="text-sm">{emp.address || "—"}</p></div>
            <div className="sm:col-span-3"><p className="emp-eyebrow mb-1">Emergency Contact</p><p className="text-sm">{emp.emergency_contact || "—"}</p></div>
          </div>
        )}
        {tab === "ets" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="emp-eyebrow">Employee Trust Score · {emp.ets_score}/1000</p>
              {!scoreEdit ? (
                <button className="emp-btn-ghost" onClick={() => setScoreEdit(true)} data-testid="emp-ets-edit">Edit</button>
              ) : (
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={1000} className="emp-input w-24" value={newScore} onChange={(e) => setNewScore(e.target.value)} data-testid="emp-ets-input" />
                  <button onClick={saveScore} disabled={saving} className="emp-btn-primary" data-testid="emp-ets-save">{saving ? "…" : "Save"}</button>
                  <button onClick={() => { setScoreEdit(false); setNewScore(emp.ets_score); }} className="emp-btn-ghost">Cancel</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(emp.ets_breakdown || {}).map(([k, v]) => (
                <div key={k} className="border p-3" style={{ borderColor: "var(--emp-border)" }}>
                  <p className="emp-eyebrow mb-1" style={{ opacity: 0.6 }}>{k.replace(/_/g, " ")}</p>
                  <p className="text-xl font-editorial">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-xs mt-4" style={{ color: "var(--emp-muted)" }}>Phase 2 will auto-calculate breakdown from live ERP activity (sales, stock-check speed, customer ratings, attendance, behaviour events, ownership tasks).</p>
          </div>
        )}
        {tab === "login-history" && (
          <div>
            <p className="emp-eyebrow mb-3">Audit trail · {audit.length} events</p>
            {audit.length === 0 ? <p className="text-xs" style={{ color: "var(--emp-muted)" }}>No events yet.</p> : (
              <ul className="text-sm divide-y" style={{ borderColor: "var(--emp-border)" }}>
                {audit.map(a => (
                  <li key={a.id} className="py-2 flex items-center justify-between">
                    <span>{a.event}</span>
                    <span className="emp-eyebrow" style={{ opacity: 0.6 }}>{new Date(a.at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {tab === "settings" && (
          <EmployeeSettingsTab
            emp={emp}
            employees={employees}
            onUpdated={(next) => setEmp(next)}
            onDeleted={onBack}
          />
        )}
        {tab === "permissions" && <PermissionsTab empId={id} />}
        {tab === "devices" && <DevicesTab empId={id} />}
        {tab === "reports" && (
          <div className="space-y-4">
            <p className="emp-eyebrow">Downloadable reports</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  fetch(`${API}/admin/exports/employees/${id}.pdf`, adminAuthHeaders())
                    .then(r => r.blob()).then(b => {
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(b);
                      link.download = `${emp.employee_code}_TileRoom_Report.pdf`;
                      link.click();
                    }).catch(() => toast.error("Failed"));
                }}
                className="emp-btn-primary inline-flex items-center gap-2"
                data-testid="emp-profile-pdf"
              >
                Download Performance PDF
              </button>
              <button
                onClick={() => {
                  fetch(`${API}/admin/exports/attendance.csv?days=90`, adminAuthHeaders())
                    .then(r => r.blob()).then(b => {
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(b);
                      link.download = `attendance_90d.csv`;
                      link.click();
                    }).catch(() => toast.error("Failed"));
                }}
                className="emp-btn-ghost inline-flex items-center gap-2"
                data-testid="emp-profile-att-csv"
              >
                Attendance CSV (90 days · all staff)
              </button>
            </div>
          </div>
        )}
        {["attendance", "sales", "cx", "behaviour", "innovation"].includes(tab) && (
          <p className="text-sm text-center py-16" style={{ color: "var(--emp-muted)" }}>
            View the full <span className="capitalize">{tab.replace(/-/g, " ")}</span> ledger under the Employees tab · sub-nav → <span className="capitalize">{tab}</span>.
          </p>
        )}
      </div>
    </div>
  );
}



// ------------------------------------------------------------
// Shipment B · Permissions matrix (per-employee)
// ------------------------------------------------------------
function PermissionsTab({ empId }) {
  const [schema, setSchema] = useState({ modules: [], actions: [] });
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: sch }, { data: emp }] = await Promise.all([
          axios.get(`${API}/admin/employees/permissions/schema`, adminAuthHeaders()),
          axios.get(`${API}/admin/employees/${empId}`, adminAuthHeaders()),
        ]);
        setSchema(sch);
        setPerms(emp.perms || {});
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
      finally { setLoading(false); }
    })();
  }, [empId]);

  const toggle = (mod, act) => {
    setPerms(p => ({ ...p, [mod]: { ...(p[mod] || {}), [act]: !((p[mod] || {})[act]) } }));
  };
  const setAllForModule = (mod, value) => {
    setPerms(p => ({ ...p, [mod]: Object.fromEntries(schema.actions.map(a => [a, value])) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/employees/${empId}/permissions`, { perms }, adminAuthHeaders());
      setPerms(data.perms || {});
      toast.success("Permissions saved (super-admin only)");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-center py-8 emp-eyebrow">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="emp-eyebrow inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" strokeWidth={1.5} /> Granular Permissions Matrix</p>
          <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>Toggle exactly what this employee can View / Create / Edit / Delete / Approve / Export in each module. Requires super-admin.</p>
        </div>
        <button onClick={save} disabled={saving} className="emp-btn-primary" data-testid="perms-save">
          {saving ? "Saving…" : "Save Permissions"}
        </button>
      </div>

      <div className="overflow-x-auto emp-glass">
        <table className="w-full text-sm" data-testid="perms-table">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--emp-border)" }}>
              <th className="p-3 emp-eyebrow">Module</th>
              {schema.actions.map(a => <th key={a} className="p-3 emp-eyebrow text-center capitalize">{a}</th>)}
              <th className="p-3 emp-eyebrow text-center">Quick</th>
            </tr>
          </thead>
          <tbody>
            {schema.modules.map(m => (
              <tr key={m.key} className="border-b" style={{ borderColor: "var(--emp-border)" }} data-testid={`perms-row-${m.key}`}>
                <td className="p-3 text-sm">{m.label}</td>
                {schema.actions.map(a => (
                  <td key={a} className="p-3 text-center">
                    <input
                      type="checkbox"
                      checked={!!((perms[m.key] || {})[a])}
                      onChange={() => toggle(m.key, a)}
                      data-testid={`perms-${m.key}-${a}`}
                      style={{ accentColor: "var(--emp-accent)" }}
                    />
                  </td>
                ))}
                <td className="p-3 text-center">
                  <button onClick={() => setAllForModule(m.key, true)} className="text-[10px] uppercase tracking-[0.15em] mr-1" style={{ color: "var(--emp-accent)" }}>All</button>
                  <button onClick={() => setAllForModule(m.key, false)} className="text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--emp-muted)" }}>None</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ------------------------------------------------------------
// Shipment B · Devices Tab (registered devices per employee)
// ------------------------------------------------------------
function DevicesTab({ empId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees/${empId}/devices`, adminAuthHeaders());
      setDevices(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [empId]);

  const trust = async (d, trusted) => {
    try { await axios.patch(`${API}/admin/employees/${empId}/devices/${d.id}`, { trusted }, adminAuthHeaders()); toast.success(trusted ? "Marked trusted" : "Untrusted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rename = async (d) => {
    const name = window.prompt("Give this device a friendly name (e.g. Warehouse Laptop)", d.device_name || "");
    if (name === null) return;
    try { await axios.patch(`${API}/admin/employees/${empId}/devices/${d.id}`, { device_name: name }, adminAuthHeaders()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (d) => {
    if (!window.confirm("Remove this device? Next login from it will be logged as new.")) return;
    try { await axios.delete(`${API}/admin/employees/${empId}/devices/${d.id}`, adminAuthHeaders()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <p className="emp-eyebrow inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" strokeWidth={1.5} /> Registered Devices · {devices.length}</p>
      <p className="text-xs" style={{ color: "var(--emp-muted)" }}>Devices are captured automatically on login. Mark trusted to whitelist, remove to force re-registration.</p>
      {loading ? <p className="emp-eyebrow py-6 text-center">Loading…</p> :
        devices.length === 0 ? <p className="emp-glass p-8 text-center text-sm" style={{ color: "var(--emp-muted)" }}>No devices registered yet.</p> :
        <ul className="space-y-2" data-testid="devices-list">
          {devices.map(d => (
            <li key={d.id} className="emp-glass p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`device-${d.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{d.device_name || <span style={{ color: "var(--emp-muted)" }}>Unnamed device</span>} {d.trusted && <span className="emp-grade emp-grade-Gold ml-1">Trusted</span>}</p>
                <p className="text-[11px] break-all mt-0.5" style={{ color: "var(--emp-muted)" }}>{d.user_agent}</p>
                <p className="text-[10px] mt-0.5" style={{ color: "var(--emp-muted)" }}>
                  IP {d.ip || "—"} · Logins {d.login_count} · Last {new Date(d.last_seen_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => rename(d)} className="emp-btn-ghost text-xs">Rename</button>
                <button onClick={() => trust(d, !d.trusted)} className="emp-btn-ghost text-xs" style={d.trusted ? { color: "#EF4444", borderColor: "#EF4444" } : { color: "var(--emp-accent)", borderColor: "var(--emp-accent)" }} data-testid={`device-trust-${d.id}`}>
                  {d.trusted ? "Untrust" : "Trust"}
                </button>
                <button onClick={() => remove(d)} className="emp-btn-ghost text-xs" style={{ color: "#EF4444", borderColor: "#EF4444" }} data-testid={`device-remove-${d.id}`}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}


// ------------------------------------------------------------
// Smart password field · admin adds employee
// (show/hide · generate strong password · copy to share)
// ------------------------------------------------------------
function PasswordField({ value, onChange, label, testId }) {
  const [show, setShow] = useState(false);
  const generate = () => {
    // 12-char password: mixed case + digit + symbol, no ambiguous chars
    const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
    const lower = "abcdefghjkmnpqrstuvwxyz";
    const digit = "23456789";
    const sym = "@#$%&*!";
    const pick = (str) => str[Math.floor(Math.random() * str.length)];
    let pw = [pick(upper), pick(lower), pick(digit), pick(sym)];
    const all = upper + lower + digit + sym;
    while (pw.length < 12) pw.push(pick(all));
    pw = pw.sort(() => Math.random() - 0.5).join("");
    onChange(pw);
    setShow(true);
  };
  const copy = () => {
    if (!value) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value)
        .then(() => toast.success("Password copied — share it with the employee"))
        .catch(() => toast.info("Copy blocked — long-press the field to copy manually"));
    } else {
      toast.info("Copy not supported here — select and copy the password manually");
    }
  };

  return (
    <div>
      <label className="emp-eyebrow block mb-2">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          className="emp-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="At least 6 characters"
          data-testid={testId}
          style={{ paddingRight: "72px" }}
        />
        <div className="absolute right-1 top-1 flex gap-0.5">
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="p-1.5 opacity-60 hover:opacity-100"
            aria-label={show ? "Hide password" : "Show password"}
            data-testid={`${testId}-toggle`}
          >
            {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={!value}
            className="p-1.5 opacity-60 hover:opacity-100 disabled:opacity-20"
            aria-label="Copy password"
            data-testid={`${testId}-copy`}
          >
            <Copy className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={generate}
          className="emp-eyebrow inline-flex items-center gap-1 hover:opacity-100 opacity-70"
          style={{ color: "var(--emp-accent)" }}
          data-testid={`${testId}-generate`}
        >
          <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Generate strong password
        </button>
        {value && value.length > 0 && (
          <span className="text-[10px]" style={{ color: value.length >= 8 ? "#10B981" : value.length >= 6 ? "#C9A968" : "#B23A3A" }}>
            {value.length >= 12 ? "Excellent" : value.length >= 8 ? "Strong" : value.length >= 6 ? "OK — could be stronger" : "Too short"}
          </span>
        )}
      </div>
    </div>
  );
}



// ------------------------------------------------------------
// Employee Settings Tab · Edit · Reset PW · Deactivate · Delete
// ------------------------------------------------------------
function EmployeeSettingsTab({ emp, employees, onUpdated, onDeleted }) {
  const [form, setForm] = useState({
    name: emp.name || "",
    email: emp.email || "",
    mobile: emp.mobile || "",
    photo_url: emp.photo_url || "",
    dob: emp.dob || "",
    gender: emp.gender || "",
    blood_group: emp.blood_group || "",
    address: emp.address || "",
    emergency_contact: emp.emergency_contact || "",
    aadhaar: emp.aadhaar || "",
    pan: emp.pan || "",
    joining_date: emp.joining_date || "",
    departments: emp.departments || [],
    designation: emp.designation || "",
    reporting_manager_id: emp.reporting_manager_id || "",
    salary: emp.salary || 0,
    employment_status: emp.employment_status || "Active",
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };
  const toggleDept = (d) => {
    setForm(f => {
      const has = f.departments.includes(d);
      return { ...f, departments: has ? f.departments.filter(x => x !== d) : [...f.departments, d] };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim() || !form.designation.trim()) {
      toast.error("Name, mobile and designation are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0, reporting_manager_id: form.reporting_manager_id || null };
      const { data } = await axios.patch(`${API}/admin/employees/${emp.id}`, payload, adminAuthHeaders());
      onUpdated(data);
      toast.success("Employee updated");
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (resetPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setResetting(true);
    try {
      await axios.post(`${API}/admin/employees/${emp.id}/reset-password`,
        { new_password: resetPw }, adminAuthHeaders());
      toast.success(`Password reset · share the new one with ${emp.name}`);
      setResetPw(""); setShowResetPw(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setResetting(false); }
  };

  const generateResetPw = () => {
    const alph = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$&*!";
    let pw = "";
    for (let i = 0; i < 12; i++) pw += alph[Math.floor(Math.random() * alph.length)];
    setResetPw(pw); setShowResetPw(true);
  };

  const deactivate = async () => {
    if (!window.confirm(`Deactivate ${emp.name}? They will not be able to log in. This is reversible — you can re-activate from the Employment Status field above.`)) return;
    setDeleting(true);
    try {
      const { data } = await axios.patch(`${API}/admin/employees/${emp.id}`,
        { employment_status: "Inactive" }, adminAuthHeaders());
      onUpdated(data);
      toast.success("Employee deactivated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setDeleting(false); }
  };

  const hardDelete = async () => {
    if (purgeConfirm !== emp.employee_code) {
      toast.error(`Type ${emp.employee_code} to confirm permanent deletion`);
      return;
    }
    setDeleting(true);
    try {
      const { data } = await axios.delete(`${API}/admin/employees/${emp.id}?permanent=true`, adminAuthHeaders());
      const rep = data.exit_report || {};
      if (rep.sent) {
        toast.success(`${emp.name} deleted · Exit Report emailed to ${rep.recipients.length} recipient(s)`);
      } else if (rep.recipients && rep.recipients.length === 0) {
        toast.success(`${emp.name} deleted · Exit Report PDF archived (add CEO recipients under Weekly CEO Report to auto-email next time)`);
      } else {
        toast.success(`${emp.name} deleted`);
      }
      onDeleted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setDeleting(false); }
  };

  const managers = employees.filter(x => x.id !== emp.id && x.employment_status === "Active");

  return (
    <div className="space-y-6" data-testid="emp-settings-tab">
      {/* Edit form */}
      <section className="emp-glass p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="emp-eyebrow inline-flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit Employee Details
          </p>
          {dirty && <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--emp-accent)" }}>Unsaved changes</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SF label="Full Name *" value={form.name} onChange={v => set("name", v)} testId="edit-name" />
          <SF label="Mobile *" value={form.mobile} onChange={v => set("mobile", v)} testId="edit-mobile" />
          <SF label="Email" value={form.email} onChange={v => set("email", v)} testId="edit-email" />
          <SF label="Photo URL" value={form.photo_url} onChange={v => set("photo_url", v)} testId="edit-photo" />
          <SF label="Date of Birth" type="date" value={form.dob} onChange={v => set("dob", v)} testId="edit-dob" />
          <SF label="Joining Date" type="date" value={form.joining_date} onChange={v => set("joining_date", v)} testId="edit-joining" />
          <div>
            <label className="emp-eyebrow block mb-2">Gender</label>
            <select className="emp-input" value={form.gender} onChange={e => set("gender", e.target.value)} data-testid="edit-gender">
              <option value="">Select…</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <SF label="Blood Group" value={form.blood_group} onChange={v => set("blood_group", v)} testId="edit-blood" placeholder="O+" />
          <SF label="Aadhaar" value={form.aadhaar} onChange={v => set("aadhaar", v)} testId="edit-aadhaar" />
          <SF label="PAN" value={form.pan} onChange={v => set("pan", v)} testId="edit-pan" />
          <SF label="Emergency Contact" value={form.emergency_contact} onChange={v => set("emergency_contact", v)} testId="edit-emergency" />
          <SF label="Address" value={form.address} onChange={v => set("address", v)} testId="edit-address" />
          <SF label="Designation *" value={form.designation} onChange={v => set("designation", v)} testId="edit-designation" />
          <div>
            <label className="emp-eyebrow block mb-2">Reporting Manager</label>
            <select className="emp-input" value={form.reporting_manager_id} onChange={e => set("reporting_manager_id", e.target.value)} data-testid="edit-manager">
              <option value="">— No manager —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.employee_code})</option>)}
            </select>
          </div>
          <SF label="Salary (₹/month)" type="number" value={form.salary} onChange={v => set("salary", v)} testId="edit-salary" />
          <div>
            <label className="emp-eyebrow block mb-2">Employment Status</label>
            <select className="emp-input" value={form.employment_status} onChange={e => set("employment_status", e.target.value)} data-testid="edit-status">
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="emp-eyebrow block mb-2">Departments</label>
            <div className="flex flex-wrap gap-2">
              {DEPARTMENTS.map(d => {
                const on = form.departments.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDept(d)}
                    className="emp-btn-ghost text-xs"
                    style={on ? { borderColor: "var(--emp-accent)", color: "var(--emp-accent)" } : {}}
                    data-testid={`edit-dept-${d}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="emp-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="edit-save"
          >
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </section>

      {/* Reset Password */}
      <section className="emp-glass p-6" data-testid="emp-admin-reset-pw">
        <p className="emp-eyebrow inline-flex items-center gap-1.5 mb-3">
          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "var(--emp-accent)" }} /> Reset Password
        </p>
        <p className="text-xs mb-4" style={{ color: "var(--emp-muted)" }}>
          Set a new password for {emp.name}. Share it via a secure channel — they can rotate it themselves from the employee portal.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <div className="relative">
            <input
              type={showResetPw ? "text" : "password"}
              className="emp-input"
              value={resetPw}
              onChange={e => setResetPw(e.target.value)}
              placeholder="New password (min 6)"
              data-testid="reset-pw-input"
              style={{ paddingRight: "36px" }}
            />
            <button type="button" onClick={() => setShowResetPw(s => !s)}
              className="absolute right-1 top-1 p-1.5 opacity-60 hover:opacity-100"
              data-testid="reset-pw-toggle" aria-label="Toggle visibility">
              {showResetPw ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={generateResetPw} className="emp-btn-ghost text-xs inline-flex items-center gap-1" data-testid="reset-pw-generate">
              <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Generate
            </button>
            <button onClick={resetPassword} disabled={resetting || resetPw.length < 6} className="emp-btn-primary text-xs disabled:opacity-50" data-testid="reset-pw-save">
              {resetting ? "…" : "Reset"}
            </button>
          </div>
        </div>
      </section>

      {/* Danger zone */}
      <section
        className="border p-6"
        style={{ borderColor: "#B23A3A", background: "rgba(178,58,58,0.04)" }}
        data-testid="emp-danger-zone"
      >
        <p className="text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1.5" style={{ color: "#B23A3A" }}>
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Danger Zone
        </p>

        <div className="mt-4 flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium">Deactivate</p>
            <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>Sets employment status to Inactive. Blocks login. Reversible.</p>
          </div>
          <button
            onClick={deactivate}
            disabled={deleting || emp.employment_status === "Inactive"}
            className="emp-btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
            style={{ borderColor: "#C9A968", color: "#C9A968" }}
            data-testid="emp-deactivate"
          >
            {emp.employment_status === "Inactive" ? "Already Inactive" : "Deactivate"}
          </button>
        </div>

        <div className="mt-4 pt-4 border-t flex flex-wrap items-start gap-4" style={{ borderColor: "rgba(178,58,58,0.3)" }}>
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium" style={{ color: "#B23A3A" }}>Delete Permanently</p>
            <p className="text-xs mt-1" style={{ color: "var(--emp-muted)" }}>
              Purges employee record, devices, tasks, attendance, behaviour and innovation history. This <strong>cannot</strong> be undone.
              <br />
              A branded <strong>Exit Report PDF</strong> is automatically generated and emailed to your CEO recipients before deletion — for compliance and audit.
            </p>
          </div>
          <button
            onClick={() => setDangerOpen(d => !d)}
            className="emp-btn-ghost inline-flex items-center gap-2"
            style={{ borderColor: "#B23A3A", color: "#B23A3A" }}
            data-testid="emp-hard-delete-open"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {dangerOpen ? "Cancel" : "Delete Permanently"}
          </button>
        </div>

        {dangerOpen && (
          <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: "rgba(178,58,58,0.3)" }} data-testid="emp-hard-delete-confirm">
            <p className="text-sm" style={{ color: "#B23A3A" }}>
              Type <code style={{ background: "rgba(178,58,58,0.15)", padding: "2px 6px", fontFamily: "monospace" }}>{emp.employee_code}</code> to confirm.
            </p>
            <input
              className="emp-input"
              value={purgeConfirm}
              onChange={e => setPurgeConfirm(e.target.value)}
              placeholder={emp.employee_code}
              data-testid="emp-hard-delete-input"
              style={{ borderColor: "#B23A3A" }}
            />
            <button
              onClick={hardDelete}
              disabled={deleting || purgeConfirm !== emp.employee_code}
              className="emp-btn-primary inline-flex items-center gap-2 disabled:opacity-40"
              style={{ background: "#B23A3A", color: "#fff" }}
              data-testid="emp-hard-delete-confirm-btn"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {deleting ? "Deleting…" : "Yes, Permanently Delete"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

// Small labelled field for the settings form
function SF({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="emp-eyebrow block mb-2">{label}</label>
      <input
        type={type}
        className="emp-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}

